import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Fade,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import notificationService from '../services/notificationService';

// Category icons mapping
const categoryIcons = {
  property: <HomeWorkIcon fontSize="small" />,
  transaction: <GavelIcon fontSize="small" />,
  task: <AssignmentIcon fontSize="small" />,
  communication: <ChatBubbleOutlineIcon fontSize="small" />,
  system: <InfoOutlinedIcon fontSize="small" />,
};

// Category colors
const categoryColors = {
  property: '#3b7b86',
  transaction: '#7c3aed',
  task: '#ea580c',
  communication: '#2563eb',
  system: '#64748b',
};

// Priority badge colors
const priorityColors = {
  urgent: '#dc2626',
  high: '#ea580c',
  normal: '#3b7b86',
  low: '#94a3b8',
};

/**
 * Format a date string into a relative "time ago" format
 */
const timeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const POLL_INTERVAL =  5 * 60 * 1000; // 5 minutes
const MAX_DROPDOWN_ITEMS = 8; // Show only recent unread items in dropdown

const NotificationBell = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef(null);

  const open = Boolean(anchorEl);

  // Fetch unread count (lightweight, for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response?.success && response?.data) {
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.debug('Failed to fetch notification count:', error);
    }
  }, []);

  // Fetch recent unread notifications for the dropdown
  const fetchRecentUnread = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, per_page: MAX_DROPDOWN_ITEMS, is_read: false };
      const response = await notificationService.getNotifications(params);
      if (response?.success && response?.data) {
        const data = response.data;
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Start/stop polling
  useEffect(() => {
    fetchUnreadCount();
    pollTimerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Handle bell click — fetch recent unread
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchRecentUnread();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark single notification as read + navigate
  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      if (notification.action_url) {
        handleClose();
        navigate(notification.action_url);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Navigate to full notifications page
  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  return (
    <>
      <Tooltip title="Notifications">
        <span>
          <IconButton
            size="large"
            onClick={handleOpen}
            sx={{ color: theme.modern.colors.modern.slate[600] }}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 380 },
            maxHeight: 520,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Notifications
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="error"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={handleMarkAllRead}>
                <DoneAllIcon fontSize="small" sx={{ color: theme.modern.colors.modern.slate[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Notification List — unread only, no tabs */}
        <Box sx={{ overflow: 'auto', maxHeight: 380 }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: theme.modern.colors.modern.slate[300], mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                You're all caught up!
              </Typography>
              <Typography variant="caption" color="text.disabled">
                No new notifications
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => (
                <Fade in key={notification.id} timeout={200} style={{ transitionDelay: `${index * 20}ms` }}>
                  <Box>
                    <ListItem
                      alignItems="flex-start"
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'rgba(59, 123, 134, 0.04)',
                        borderLeft: `3px solid ${priorityColors[notification.priority] || priorityColors.normal}`,
                        '&:hover': {
                          bgcolor: 'rgba(59, 123, 134, 0.08)',
                        },
                        py: 1.25,
                        px: 2,
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40, mt: 0.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `${categoryColors[notification.category] || categoryColors.system}15`,
                            color: categoryColors[notification.category] || categoryColors.system,
                          }}
                        >
                          {categoryIcons[notification.category] || categoryIcons.system}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                lineHeight: 1.3,
                                flex: 1,
                                color: 'text.primary',
                              }}
                            >
                              {notification.title}
                            </Typography>
                            <CircleIcon
                              sx={{
                                fontSize: 8,
                                color: priorityColors[notification.priority] || priorityColors.normal,
                                flexShrink: 0,
                              }}
                            />
                            {notification.priority === 'urgent' && (
                              <PriorityHighIcon sx={{ fontSize: 14, color: priorityColors.urgent, flexShrink: 0 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 0.5,
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}
                            >
                              {timeAgo(notification.created_at)}
                              {notification.actor_name ? ` \u2022 ${notification.actor_name}` : ''}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider sx={{ ml: 0 }} />
                    )}
                  </Box>
                </Fade>
              ))}
            </List>
          )}
        </Box>

        {/* Footer — View All link */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            size="small"
            onClick={handleViewAll}
            endIcon={<ArrowForwardIcon sx={{ fontSize: '0.875rem !important' }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: theme.modern.colors.modern.primary,
              '&:hover': {
                bgcolor: 'rgba(59, 123, 134, 0.06)',
              },
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
